import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, Send, AlertCircle, CheckCircle, Clock, Globe } from "lucide-react";
import emailjs from '@emailjs/browser';

const Contact = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [message, setMessage] = useState<{ content: string; type: 'success' | 'error' } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    if (!formRef.current) return;

    emailjs.sendForm('service_obkwpg6', 'template_uqnlxfp', formRef.current, 'rSsLKVvJyAgMvlIk3')
      .then(() => {
        setMessage({ content: 'Message envoyé avec succès !', type: 'success' });
        formRef.current?.reset();
      })
      .catch((error: { text: string }) => {
        setMessage({ content: `Erreur lors de l'envoi: ${error.text}`, type: 'error' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#1E3D1A]">
        <div className="absolute inset-0 bg-[url('/images/carrousel3.webp')] opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Contactez-nous
            </h1>
            <p className="text-xl text-white/90">
              Nous sommes là pour répondre à toutes vos questions
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
              {/* Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="h-full"
              >
                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 h-full flex flex-col">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Informations de contact</h2>
                  
                  <div className="space-y-6 flex-grow">
                    <div className="flex items-start space-x-4 group">
                      <div className="bg-green-100 p-3 rounded-full group-hover:bg-green-200 transition-colors duration-300">
                        <MapPin className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Adresse</h3>
                        <p className="text-gray-600">
                          Diamniadio cité Senegindia Villa 009-TYPE A
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 group">
                      <div className="bg-green-100 p-3 rounded-full group-hover:bg-green-200 transition-colors duration-300">
                        <Phone className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Téléphone</h3>
                        <p className="text-gray-600">+221 33 944 09 43 | 76 624 85 05</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 group">
                      <div className="bg-green-100 p-3 rounded-full group-hover:bg-green-200 transition-colors duration-300">
                        <Mail className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Email</h3>
                        <p className="text-gray-600">info@pmn.sn | info.sunuyartisan@pmn.sn</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 group">
                      <div className="bg-green-100 p-3 rounded-full group-hover:bg-green-200 transition-colors duration-300">
                        <Clock className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Horaires</h3>
                        <p className="text-gray-600">Lundi - Vendredi: 8h00 - 18h00</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 group">
                      <div className="bg-green-100 p-3 rounded-full group-hover:bg-green-200 transition-colors duration-300">
                        <Globe className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Site Web</h3>
                        <p className="text-gray-600">www.pmn.sn | www.sunuyartisan.pmn.sn</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="h-full"
              >
                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 h-full flex flex-col">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>
                  
                  <AnimatePresence>
                    {message && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className={`p-4 rounded-lg mb-6 flex items-center space-x-3 ${
                          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                        }`}
                      >
                        {message.type === 'success' ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <AlertCircle className="w-5 h-5" />
                        )}
                        <p>{message.content}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form ref={formRef} onSubmit={sendEmail} className="space-y-6 flex-grow flex flex-col">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <input
                          required
                          type="text"
                          name="name"
                          placeholder="Nom complet*"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all hover:border-green-300"
                        />
                      </div>
                      <div>
                        <input
                          required
                          type="text"
                          name="company"
                          placeholder="Entreprise*"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all hover:border-green-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <input
                          required
                          type="tel"
                          name="number"
                          placeholder="Numéro de téléphone*"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all hover:border-green-300"
                        />
                      </div>
                      <div>
                        <input
                          required
                          type="email"
                          name="email"
                          placeholder="E-mail*"
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all hover:border-green-300"
                        />
                      </div>
                    </div>

                    <div>
                      <input
                        type="text"
                        name="subject"
                        placeholder="Objet"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all hover:border-green-300"
                      />
                    </div>

                    <div className="flex-grow">
                      <textarea
                        required
                        name="Votre message"
                        placeholder="Votre message*"
                        className="w-full h-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all hover:border-green-300"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Envoyer le message</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </motion.div>
            </div>

            {/* Map Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Notre emplacement</h2>
              <div className="relative w-full h-[500px] rounded-xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!4v1711901064531!6m8!1m7!1siLm2HlfrJLVK10BgJ6F9uw!2m2!1d14.72819916981452!2d-17.20890573698145!3f159.80177536981452!4f-11.130630981841378!5f0.7820865974627469"
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
